import { z } from "zod";
import { type SupplyChainBooking } from "../BookingsTable";
import { type Condition, type OwnershipType } from "./constants";
import { type FieldSetting, type MetadataField } from "./types";
import { type SexClass } from "@/benchmarks/BenchmarkFilters/SexPicker";
import { getRequiredFieldMessage } from "@/lib/utils/validation";
import {
  createOptionalIntegerSchema,
  createOptionalStringSchema,
  createRequiredIntegerSchema,
  createRequiredStringSchema,
} from "@/lib/utils/validation/zod";

export type BookingStatus = NonNullable<SupplyChainBooking>["status"];

const bookingStatuses = [
  "ARCHIVED",
  "AWAITING_PAYMENT",
  "BOOKED",
  "CANCELLED",
  "CLOSED",
  "PAID",
  "PLANNED",
  "SHIPPED",
  "DRAFT",
  "CLOSED",
] satisfies Array<BookingStatus>;

const MAX_TEXT_INPUT_LENGTH = 30;
const MAX_TEXT_AREA_LENGTH = 500;

export const getEffectiveFieldRequirements = (settings: FieldSetting[]) => {
  return settings.reduce<{ [K in MetadataField]?: { isEffectivelyRequired: boolean } }>((acc, setting) => {
    const { field } = setting;
    return {
      ...acc,
      [field]: {
        isEffectivelyRequired: setting.isVisible && setting.isRequired,
      },
    };
  }, {});
};

const baseBookingSchema = z.object({
  name: createRequiredStringSchema("name", 50),
  numberOfHead: z.coerce
    .number({ invalid_type_error: "Number of Head is required.", required_error: "Number of Head is required." })
    .int({ message: "Number of Head must be a whole number." })
    .positive({ message: "Number of Head must be at least 1." })
    .min(1, { message: "Number of Head is required." })
    .max(100000, "Number of Head cannot exceed 100,000."),
  status: z
    .enum(bookingStatuses as [BookingStatus, ...BookingStatus[]], {
      required_error: getRequiredFieldMessage("status"),
    })
    .refine((value) => value !== undefined, {
      message: getRequiredFieldMessage("status"),
    }),
  plannedShipmentDate: z.date({
    required_error: getRequiredFieldMessage("planned shipment date"),
    invalid_type_error: "Please enter a valid date",
  }),
  note: z.string().max(500).optional(),
  destination: z.string().optional().nullable(),
  businessAccountManagerId: z.string().optional().nullable(),
  source: z.string().optional().nullable(),
});

const createMetadataSchema = (settings: FieldSetting[]) => {
  const effectiveFieldRequirements = getEffectiveFieldRequirements(settings);

  const validationRules = {
    SEX_CLASS: effectiveFieldRequirements["SEX_CLASS"]?.isEffectivelyRequired
      ? z.enum(["STEER", "HEIFER", "MIXED"] as const satisfies Array<SexClass>, {
          message: "Sex class is required.",
        })
      : z.preprocess(
          (val) => {
            if (typeof val === "string" && val.trim() === "") {
              return undefined;
            }
            return val;
          },
          z.enum(["STEER", "HEIFER", "MIXED"] as const satisfies Array<SexClass>).optional(),
        ),
    PRICE: effectiveFieldRequirements["PRICE"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Price", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Price", MAX_TEXT_INPUT_LENGTH),
    PURCHASE_PRICE_PER_CWT: effectiveFieldRequirements["PURCHASE_PRICE_PER_CWT"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Price per CWT", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Price per CWT", MAX_TEXT_INPUT_LENGTH),
    PEN_NUMBER: effectiveFieldRequirements["PEN_NUMBER"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Pen number", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Pen number", MAX_TEXT_INPUT_LENGTH),
    TRUCKING_COMPANY: effectiveFieldRequirements["TRUCKING_COMPANY"]?.isEffectivelyRequired
      ? z
          .string({ message: "Trucking company is required." })
          .uuid({ message: "Invalid trucking company ID format" })
          .min(1, { message: "Trucking company is required." })
      : z.string().uuid({ message: "Invalid trucking company ID format" }).nullish(),
    GRID: effectiveFieldRequirements["GRID"]?.isEffectivelyRequired
      ? z
          .string({ message: "Grid is required." })
          .uuid({ message: "Invalid grid ID format" })
          .min(1, { message: "Grid name is required." })
      : z.string().uuid({ message: "Invalid grid ID format" }).nullish(),
    PROGRAM: effectiveFieldRequirements["PROGRAM"]?.isEffectivelyRequired
      ? z
          .string({ message: "Program is required." })
          .uuid({ message: "Invalid program ID format" })
          .min(1, { message: "Program is required." })
      : z.string().uuid({ message: "Invalid program ID format" }).nullish(),
    BROKER: effectiveFieldRequirements["BROKER"]?.isEffectivelyRequired
      ? z
          .string({ message: "Broker is required." })
          .uuid({ message: "Invalid broker ID format" })
          .min(1, { message: "Broker is required." })
      : z.string().uuid({ message: "Invalid broker ID format" }).nullish(),
    ACCREDITATIONS: effectiveFieldRequirements["ACCREDITATIONS"]?.isEffectivelyRequired
      ? z
          .array(z.string().uuid({ message: "Invalid accreditation ID format" }))
          .min(1, { message: "Accreditations are required." })
      : z.array(z.string().uuid({ message: "Invalid accreditation ID format" })).optional(),
    CONFIRMED: effectiveFieldRequirements["CONFIRMED"]?.isEffectivelyRequired ? z.boolean() : z.boolean().optional(),
    ESTIMATED_WEIGHT_NOTE: effectiveFieldRequirements["ESTIMATED_WEIGHT_NOTE"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Est. weight note", MAX_TEXT_AREA_LENGTH)
      : createOptionalStringSchema("Est. weight note", MAX_TEXT_AREA_LENGTH),
    DATE_OF_SALE: effectiveFieldRequirements["DATE_OF_SALE"]?.isEffectivelyRequired
      ? z.date({
          required_error: getRequiredFieldMessage("date of sale"),
          invalid_type_error: "Please enter a valid date.",
        })
      : z
          .date({
            invalid_type_error: "Please enter a valid date.",
          })
          .optional(),
    DATE_OF_VISIT: effectiveFieldRequirements["DATE_OF_VISIT"]?.isEffectivelyRequired
      ? z.date({
          required_error: getRequiredFieldMessage("date of visit"),
          invalid_type_error: "Please enter a valid date.",
        })
      : z
          .date({
            invalid_type_error: "Please enter a valid date.",
          })
          .optional(),
    SOURCE_NOTE: effectiveFieldRequirements["SOURCE_NOTE"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Source note", MAX_TEXT_AREA_LENGTH)
      : createOptionalStringSchema("Source note", MAX_TEXT_AREA_LENGTH),
    BREED_NOTE: effectiveFieldRequirements["BREED_NOTE"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Breed note", MAX_TEXT_AREA_LENGTH)
      : createOptionalStringSchema("Breed note", MAX_TEXT_AREA_LENGTH),
    BRAND_NOTE: effectiveFieldRequirements["BRAND_NOTE"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Brand note", MAX_TEXT_AREA_LENGTH)
      : createOptionalStringSchema("Brand note", MAX_TEXT_AREA_LENGTH),
    ACCOUNT_NUMBER: effectiveFieldRequirements["ACCOUNT_NUMBER"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Account number", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Account number", MAX_TEXT_INPUT_LENGTH),
    CERTIFICATE_NUMBER: effectiveFieldRequirements["CERTIFICATE_NUMBER"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Certificate number", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Certificate number", MAX_TEXT_INPUT_LENGTH),
    LOT_NUMBER: effectiveFieldRequirements["LOT_NUMBER"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Lot number", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Lot number", MAX_TEXT_INPUT_LENGTH),
    SERVICE_PROVIDED: effectiveFieldRequirements["SERVICE_PROVIDED"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Service provided", MAX_TEXT_AREA_LENGTH)
      : createOptionalStringSchema("Service provided", MAX_TEXT_AREA_LENGTH),
    DATE_DELIVERED: effectiveFieldRequirements["DATE_DELIVERED"]?.isEffectivelyRequired
      ? z.date({
          required_error: getRequiredFieldMessage("date delivered"),
          invalid_type_error: "Please enter a valid date.",
        })
      : z
          .date({
            invalid_type_error: "Please enter a valid date.",
          })
          .optional(),
    SHIPPED_WEIGHT: effectiveFieldRequirements["SHIPPED_WEIGHT"]?.isEffectivelyRequired
      ? createRequiredIntegerSchema({
          displayName: "Shipped Weight",
          min: 1,
          max: 1000000,
        })
      : createOptionalIntegerSchema({
          displayName: "Shipped Weight",
          min: 1,
          max: 1000000,
        }),
    BASE_WEIGHT: effectiveFieldRequirements["BASE_WEIGHT"]?.isEffectivelyRequired
      ? createRequiredIntegerSchema({
          displayName: "Base Weight",
          min: 1,
          max: 1000000,
        })
      : createOptionalIntegerSchema({
          displayName: "Base Weight",
          min: 1,
          max: 1000000,
        }),
    SHRINK_PERCENT: effectiveFieldRequirements["SHRINK_PERCENT"]?.isEffectivelyRequired
      ? createRequiredIntegerSchema({
          displayName: "Shrink Percentage",
          min: 1,
          max: 100,
        })
      : createOptionalIntegerSchema({
          displayName: "Shrink Percentage",
          min: 1,
          max: 100,
        }),
    ESTIMATED_CHOICE_PERCENT: effectiveFieldRequirements["ESTIMATED_CHOICE_PERCENT"]?.isEffectivelyRequired
      ? createRequiredIntegerSchema({
          displayName: "Estimated Choice",
          min: 1,
          max: 100,
        })
      : createOptionalIntegerSchema({
          displayName: "Estimated Choice",
          min: 1,
          max: 100,
        }),
    VETERINARIAN: effectiveFieldRequirements["VETERINARIAN"]?.isEffectivelyRequired
      ? z.string({ message: "Veterinarian is required." }).min(1, { message: "Veterinarian is required." })
      : z.string().nullish(),
    FREIGHT_COST: effectiveFieldRequirements["FREIGHT_COST"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Freight", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Freight", MAX_TEXT_INPUT_LENGTH),
    SLIDE: effectiveFieldRequirements["SLIDE"]?.isEffectivelyRequired
      ? createRequiredStringSchema("Slide", MAX_TEXT_INPUT_LENGTH)
      : createOptionalStringSchema("Slide", MAX_TEXT_INPUT_LENGTH),
    RISK_MANAGEMENT: effectiveFieldRequirements["RISK_MANAGEMENT"]?.isEffectivelyRequired
      ? z.boolean()
      : z.boolean().optional(),
    LABELS: effectiveFieldRequirements["LABELS"]?.isEffectivelyRequired
      ? z.array(z.string().uuid({ message: "Invalid label ID format" })).min(1, { message: "Labels are required." })
      : z.array(z.string().uuid({ message: "Invalid label ID format" })).optional(),
    CONDITION: effectiveFieldRequirements["CONDITION"]?.isEffectivelyRequired
      ? z.enum(
          [
            "THIN",
            "MEDIUM_LIGHT",
            "MEDIUM",
            "MEDIUM_PLUS",
            "FLESHY",
            "UNWEANED",
            "WEANED",
            "YEARLING",
            "CALF",
          ] as const satisfies Array<Condition>,
          {
            message: "Condition is required.",
          },
        )
      : z
          .enum(
            [
              "THIN",
              "MEDIUM_LIGHT",
              "MEDIUM",
              "MEDIUM_PLUS",
              "FLESHY",
              "UNWEANED",
              "WEANED",
              "YEARLING",
              "CALF",
            ] as const satisfies Array<Condition>,
            {
              message: "Please select a valid condition.",
            },
          )
          .optional(),
    CONDITIONS: effectiveFieldRequirements["CONDITIONS"]?.isEffectivelyRequired
      ? z
          .array(
            z.enum(
              [
                "THIN",
                "MEDIUM_LIGHT",
                "MEDIUM",
                "MEDIUM_PLUS",
                "FLESHY",
                "UNWEANED",
                "WEANED",
                "YEARLING",
                "CALF",
              ] as const satisfies Array<Condition>,
              {
                message: "Please select valid conditions.",
              },
            ),
          )
          .min(1, { message: "Conditions are required." })
      : z
          .array(
            z.enum(
              [
                "THIN",
                "MEDIUM_LIGHT",
                "MEDIUM",
                "MEDIUM_PLUS",
                "FLESHY",
                "UNWEANED",
                "WEANED",
                "YEARLING",
                "CALF",
              ] as const satisfies Array<Condition>,
              {
                message: "Please select valid conditions.",
              },
            ),
          )
          .optional(),
    OWNERSHIP_TYPE: effectiveFieldRequirements["OWNERSHIP_TYPE"]?.isEffectivelyRequired
      ? z.enum(["COMPANY", "CUSTOMER", "PARTNERSHIP"] as const satisfies Array<OwnershipType>, {
          message: "Ownership type is required.",
        })
      : z.preprocess(
          (val) => {
            if (typeof val === "string" && val.trim() === "") {
              return undefined;
            }
            return val;
          },
          z.enum(["COMPANY", "CUSTOMER", "PARTNERSHIP"] as const satisfies Array<OwnershipType>).optional(),
        ),
    PROJECTED_OUT_DATE: effectiveFieldRequirements["PROJECTED_OUT_DATE"]?.isEffectivelyRequired
      ? z.date({
          required_error: getRequiredFieldMessage("projected out date"),
          invalid_type_error: "Please enter a valid date.",
        })
      : z
          .date({
            invalid_type_error: "Please enter a valid date.",
          })
          .optional(),
  } satisfies Record<MetadataField, any>;

  return z.object(validationRules);
};

export const createBookingFormSchema = (settings: FieldSetting[]) => {
  const metadataSchema = createMetadataSchema(settings);

  return baseBookingSchema.merge(metadataSchema);
};

export type BookingFormSchema = z.infer<ReturnType<typeof createBookingFormSchema>>;
export type BookingSchema = BookingFormSchema;
export const bookingSchema = baseBookingSchema;
